import { ChangeEvent, useState } from "react";
import SupplierService from "../../services/SupplierService";
import ISupplierSettings from "../../models/ISupplierSettings";
import IPackingListViewOptions from "../../models/IPackingListViewOptions";

/**
 * Custom hook to manage packing list view options
 * @param {Object} supplierSettings - Supplier settings object
 * @returns {Array} - [viewOptions, handleViewOptionChange, setViewOptions]
 */
export const usePackingListViewOptions = (
    supplierSettings: ISupplierSettings
): [
    IPackingListViewOptions,
    (e: React.ChangeEvent<HTMLInputElement>) => void,
    React.Dispatch<React.SetStateAction<IPackingListViewOptions>>,
] => {
    // Initialize view options state
    const [viewOptions, setViewOptions] = useState<IPackingListViewOptions>({
        showPhone: SupplierService.getSupplierSetting(supplierSettings, "show_phone", false),
        showEmail: SupplierService.getSupplierSetting(supplierSettings, "show_email", true),
        showOrderQty: SupplierService.getSupplierSetting(supplierSettings, "show_order_qty", true),
        showShipQty: SupplierService.getSupplierSetting(supplierSettings, "show_ship_qty", true),
        showDropshipment: SupplierService.getSupplierSetting(supplierSettings, "show_dropshipment", false),
        showOrderDescription: SupplierService.getSupplierSetting(supplierSettings, "show_order_desc", false),
        showItemPhoto: SupplierService.getSupplierSetting(supplierSettings, "show_item_photo", false),
        showSku: SupplierService.getSupplierSetting(supplierSettings, "show_sku", false),
        showUpc: SupplierService.getSupplierSetting(supplierSettings, "show_upc", false),
        hideSkuCol: SupplierService.getSupplierSetting(supplierSettings, "hide_sku_col", false),
        showBillTo: SupplierService.getSupplierSetting(supplierSettings, "show_bill_to", false),
        showShipToPhone: SupplierService.getSupplierSetting(supplierSettings, "show_ship_to_phone", false),
        showBillToPhone: SupplierService.getSupplierSetting(supplierSettings, "show_bill_to_phone", false),
        showOrderUser: SupplierService.getSupplierSetting(supplierSettings, "show_order_user", false),
        showItemLocation: SupplierService.getSupplierSetting(supplierSettings, "show_item_location", false),
        showUom: SupplierService.getSupplierSetting(supplierSettings, "show_uom", false),
        hideBusinessName: SupplierService.getSupplierSetting(supplierSettings, "hide_business_name", false),
        inlineLogoName: SupplierService.getSupplierSetting(supplierSettings, "inline_logo_name", false),
        showDimensions: SupplierService.getSupplierSetting(supplierSettings, "show_dimensions", false),
        showPickQty: SupplierService.getSupplierSetting(supplierSettings, "show_pick_qty", false),
        showOrder: SupplierService.getSupplierSetting(supplierSettings, "show_order", false),
        showLot: SupplierService.getSupplierSetting(supplierSettings, "show_lot", false),
        showItemName: SupplierService.getSupplierSetting(supplierSettings, "show_item_name", false),
        showPackingListName: SupplierService.getSupplierSetting(supplierSettings, "show_packing_list_name", false),
        showUnitPrice: SupplierService.getSupplierSetting(supplierSettings, "show_unit_price", false),
    });

    // Handle option changes
    const handleViewOptionChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        setViewOptions((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Return both the state and the handler
    return [viewOptions, handleViewOptionChange, setViewOptions];
};
